import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { parseZonedDateTime } from '@internationalized/date';
import {
  useSuggestedMutations,
  useConvertSuggestedRoutes,
} from '@lp/features/RoutePlanner/components/SuggestedRoutes/hooks';
import { showGenerationError } from '@lp/features/RoutePlanner/utils';
import { RoutePlanType } from '@/types/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';

type UseBenchmarkRouteReturnType = {
  isBenchmarkLoading: boolean;
  showBenchmark: boolean;
  benchmarkPlan: RoutePlanType | null;
  fetchBenchmark: (isTmdxDriveCompatible: boolean) => Promise<void>;
  setShowBenchmark: (show: boolean) => void;
};

export const useBenchmarkRoute = (): UseBenchmarkRouteReturnType => {
  const [isBenchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkPlan, setBenchmarkPlan] = useState<RoutePlanType | null>(null);
  const [showBenchmark, setShowBenchmark] = useState(true);

  const {
    caseId,
    donorType,
    organType,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
  } = useBaseCaseDetails();
  const { createBenchmarkRouteTask } = useSuggestedMutations();
  const { getBenchmarkRoute } = useConvertSuggestedRoutes();

  const fetchBenchmark = useCallback(
    async (isTmdxDriveCompatible: boolean) => {
      try {
        setBenchmarkLoading(true);
        const formatted = format(new Date(`${donorHospitalDate} ${donorHospitalTime}`), `yyyy-MM-dd'T'HH:mm:ss`);
        const orTime = parseZonedDateTime(`${formatted}[${donorHospitalTimeZone}]`).toAbsoluteString();
        const orStringWithoutMs = orTime.match(/[^.]*/)?.[0];
        const orTimeShortUtcString = `${orStringWithoutMs}Z`;

        const taskId = await createBenchmarkRouteTask({
          hospitalId: donorHospital!.id,
          txCenterId: txCenter!.id,
          caseType: donorType,
          organType,
          orTime: orTimeShortUtcString,
          caseId,
        });
        const plan = await getBenchmarkRoute({ planId: taskId, isTmdxDriveCompatible });
        setBenchmarkPlan(plan);
      } catch (e) {
        showGenerationError(e);
      } finally {
        setBenchmarkLoading(false);
      }
    },
    [
      caseId,
      createBenchmarkRouteTask,
      donorHospital,
      donorHospitalDate,
      donorHospitalTime,
      donorHospitalTimeZone,
      donorType,
      getBenchmarkRoute,
      organType,
      txCenter,
    ],
  );

  return {
    isBenchmarkLoading,
    showBenchmark,
    benchmarkPlan,
    fetchBenchmark,
    setShowBenchmark,
  };
};
